/**
 * Gedeelde hulpprogramma's voor het vergelijken en onthullen van getypte
 * memory-antwoorden tegen canonieke metadata-tekst (kanaal-namen, kanaal-
 * omschrijvingen, gate-keynotes, enz.). Door deze logica op één plaats te
 * houden gedragen alle memory-modi (Kanalenjacht, Poortenjacht, …) zich
 * consistent qua case-/diakriet-tolerantie, lidwoord-tolerantie, fuzzy
 * matching, autocomplete en hint-onthulling.
 *
 * Geen runtime-staat — uitsluitend pure functies + constanten.
 */

/**
 * Lidwoorden die we als "ruis" beschouwen bij het vergelijken
 * van memory-antwoorden, het matchen van autocomplete-woorden, en het
 * tellen van "echte" letters voor hints. Hun aan- of afwezigheid mag nooit
 * doorslaggevend zijn voor goed/fout. Voor hints geldt bovendien dat een
 * lidwoord op het pad gratis mee onthuld wordt zodat tussenliggende
 * woorden niet als losse letters in de weergave verschijnen.
 */
export const FILLER_WORDS: ReadonlySet<string> = new Set(['de', 'het', 'een', 'van', 'the', 'a', 'of', 'in']);

/**
 * Tolerantie-instellingen voor de fuzzy-vergelijking (Levenshtein-afstand)
 * tussen getypte tekst en doelwit. De maximaal toegelaten edit-distance
 * schaalt met de doelwit-lengte: ~`MEMORY_FUZZY_MAX_RATIO` van de lengte,
 * begrensd op `MEMORY_FUZZY_MAX_DISTANCE`. Voor zeer korte doelwitten
 * (lengte < `MEMORY_FUZZY_MIN_TARGET_LENGTH`) eisen we een exact-match om
 * valse positieven te vermijden.
 */
export const MEMORY_FUZZY_MAX_RATIO = 0.2;
export const MEMORY_FUZZY_MAX_DISTANCE = 3;
export const MEMORY_FUZZY_MIN_TARGET_LENGTH = 4;

/** Aantal letters dat één hint-aanvraag in een memory-modus onthult. */
export const MEMORY_HINT_LETTERS_PER_REQUEST = 2;

/**
 * Minimum-lengte van een woord dat meetelt voor de memory-autocomplete-match.
 * Korter dan dit zijn typisch lidwoorden of voegwoorden zonder onderscheidende
 * waarde tegen het doelwit.
 */
export const MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH = 3;

/**
 * Aantal getypte woorden (ongeacht volgorde) dat in een variant van het
 * doelwit gevonden moet worden voordat de canonieke spelling als
 * autocomplete-suggestie wordt aangeboden.
 */
export const MEMORY_AUTOCOMPLETE_MIN_MATCHES = 2;

/**
 * Drempel (verhouding 0..1) waarboven de getypte tekst als "voldoende dicht
 * bij de oplossing" telt om de canonieke spelling als autocomplete-suggestie
 * aan te bieden. Berekend als `1 - levenshtein / max(lengtes)` op de
 * filler-vrije, genormaliseerde vormen.
 */
export const MEMORY_AUTOCOMPLETE_MIN_SIMILARITY = 0.68;

/**
 * Normaliseert een door de speler getypte tekst voor case-insensitieve en
 * diakriet-onafhankelijke vergelijking met metadata. Trimt en collapseert
 * opeenvolgende whitespace tot één spatie.
 */
export function normalizeMemoryInput(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Verwijdert standalone Nederlandse lidwoorden uit een reeds-genormaliseerde
 * tekst. Wordt zowel bij de fuzzy-validatie als bij de autocomplete
 * (woord-overlap én similariteit) gebruikt zodat extra of ontbrekende
 * lidwoorden geen invloed hebben op goed/fout. Dubbele spaties die door
 * verwijderingen kunnen ontstaan worden tot één spatie collapsed.
 */
export function stripFillerWords(normalized: string): string {
  if (normalized.length === 0) {
    return '';
  }
  return normalized
    .split(' ')
    .filter(word => word.length > 0 && !FILLER_WORDS.has(word))
    .join(' ');
}

/**
 * Extraheert alle "betekenisvolle" woorden uit een tekst voor de
 * autocomplete-matching: woorden korter dan
 * `MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH` én Nederlandse lidwoorden worden
 * weggefilterd, splitsing gebeurt op alles wat geen letter of cijfer is.
 */
export function extractMatchableWords(value: string): readonly string[] {
  return normalizeMemoryInput(value)
    .split(/[^a-z0-9]+/)
    .filter(word =>
      word.length >= MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH && !FILLER_WORDS.has(word),
    );
}

/**
 * Bouwt de varianten van een doelwit-tekst waarop de speler een match mag
 * scoren. Naast de originele spelling worden twee parenthese-loze vormen
 * toegevoegd zodat de inhoud tussen haakjes optioneel is:
 *   • haakjes + inhoud volledig weggelaten → "Mutatie (Pulse)" → "Mutatie";
 *   • enkel de haakjes-tekens weggelaten   → "(H)erkenning" → "Herkenning".
 * Wanneer een doelwit geen haakjes bevat, wordt enkel de originele tekst
 * teruggegeven. Duplicaten worden uitgefilterd.
 */
export function targetMatchVariants(target: string): readonly string[] {
  if (target.indexOf('(') === -1 && target.indexOf(')') === -1) {
    return [target];
  }
  const withoutParenContent = target.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const withoutParenChars = target.replace(/[()]/g, '');
  const variants: string[] = [target];
  if (withoutParenContent.length > 0 && withoutParenContent !== target) {
    variants.push(withoutParenContent);
  }
  if (withoutParenChars !== target && !variants.includes(withoutParenChars)) {
    variants.push(withoutParenChars);
  }
  return variants;
}

/**
 * `true` zodra de speler minstens `MEMORY_AUTOCOMPLETE_MIN_MATCHES`
 * matchbare woorden (in willekeurige volgorde) heeft getypt die ook in een
 * van de varianten van `target` voorkomen (zie `targetMatchVariants` voor
 * de haakjes-tolerante varianten). Lidwoorden en woorden korter dan
 * `MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH` worden weggefilterd.
 */
export function matchesByWordOverlap(typed: string, target: string): boolean {
  const typedWords = extractMatchableWords(typed);
  if (typedWords.length < MEMORY_AUTOCOMPLETE_MIN_MATCHES) {
    return false;
  }
  const targetWords = new Set<string>();
  for (const variant of targetMatchVariants(target)) {
    for (const word of extractMatchableWords(variant)) {
      targetWords.add(word);
    }
  }
  let matches = 0;
  for (const word of typedWords) {
    if (targetWords.has(word)) {
      matches++;
      if (matches >= MEMORY_AUTOCOMPLETE_MIN_MATCHES) {
        return true;
      }
    }
  }
  return false;
}

/**
 * `true` zodra de getypte tekst dicht genoeg bij minstens één variant van
 * het doelwit zit (`>= MEMORY_AUTOCOMPLETE_MIN_SIMILARITY` op de filler-vrije,
 * genormaliseerde vormen, gemeten als `1 - levenshtein / max(lengtes)`).
 * Hierdoor verschijnt de suggestie ook wanneer de speler bijna de volledige
 * omschrijving heeft uitgetypt of de inhoud tussen haakjes weglaat.
 */
export function matchesBySimilarity(typed: string, target: string): boolean {
  const a = stripFillerWords(normalizeMemoryInput(typed));
  if (a.length === 0) {
    return false;
  }
  for (const variant of targetMatchVariants(target)) {
    const b = stripFillerWords(normalizeMemoryInput(variant));
    if (b.length === 0) {
      continue;
    }
    const distance = levenshteinDistance(a, b);
    const similarity = 1 - distance / Math.max(a.length, b.length);
    if (similarity >= MEMORY_AUTOCOMPLETE_MIN_SIMILARITY) {
      return true;
    }
  }
  return false;
}

/**
 * Vergelijkt getypte tekst met een doelwit met tolerantie voor lichte
 * typfouten, voor Nederlandse lidwoorden ('de', 'het', 'een', 'van') én
 * voor optionele inhoud tussen haakjes (zie `targetMatchVariants`).
 * Beide kanten worden eerst genormaliseerd (case + diakriet) en hun
 * lidwoorden worden verwijderd, zodat een teveel of een gebrek aan
 * lidwoorden geen fout antwoord oplevert. Daarna volgt een
 * Levenshtein-vergelijking met een lengte-afhankelijke drempel
 * (`MEMORY_FUZZY_*`-constanten); voor zeer korte doelwitten wordt strikt
 * gelijkheid vereist om valse positieven te vermijden.
 */
export function fuzzyMemoryMatch(typed: string, target: string): boolean {
  const a = stripFillerWords(normalizeMemoryInput(typed));
  return targetMatchVariants(target).some(variant => fuzzyMatchSingleTarget(a, variant));
}

function fuzzyMatchSingleTarget(normalizedTyped: string, target: string): boolean {
  const b = stripFillerWords(normalizeMemoryInput(target));
  if (normalizedTyped === b) {
    return true;
  }
  if (b.length < MEMORY_FUZZY_MIN_TARGET_LENGTH) {
    return false;
  }
  const tolerance = Math.min(
    MEMORY_FUZZY_MAX_DISTANCE,
    Math.max(1, Math.floor(b.length * MEMORY_FUZZY_MAX_RATIO)),
  );
  return levenshteinDistance(normalizedTyped, b) <= tolerance;
}

/**
 * Klassieke Levenshtein-afstand met twee rolling rows. Werkt op de
 * (reeds genormaliseerde) strings die `fuzzyMemoryMatch` aanlevert.
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) {
    prev[j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Onthult per hint-slot één "echte" letter uit `target` bovenop wat de
 * speler reeds gematcht heeft in `current`. Spaties en Nederlandse
 * lidwoorden ('de', 'het', 'een', 'van') tellen niet mee voor het budget
 * en worden gratis mee onthuld zodra ze op het pad liggen — zo zie je in de
 * UI volwaardige woordvormen ("Een d") in plaats van losse partials van
 * een lidwoord ("E").
 *
 * Geeft `null` wanneer alle echte letters van `target` reeds in `current`
 * voorkomen (er valt niets meer te onthullen, dus geen score-impact).
 */
export function revealNextChar(current: string, target: string): string | null {
  const matchedRealChars = countMatchedRealChars(current, target);
  const totalRealChars = countRealChars(target);
  if (matchedRealChars >= totalRealChars) {
    return null;
  }

  let pos = 0;
  let realCount = 0;
  while (pos < target.length) {
    if (isAtFillerWordStart(target, pos)) {
      pos = endOfWordPosition(target, pos);
      continue;
    }
    if (target.charAt(pos) === ' ') {
      pos++;
      continue;
    }
    if (realCount === matchedRealChars) {
      pos++;
      realCount++;
      break;
    }
    pos++;
    realCount++;
  }

  return target.substring(0, pos);
}

/**
 * Aantal "echte" letters waarmee `current` vooraan overeenkomt met `target`'s
 * filler-vrije real-char stream. Wandelt door `current` karakter per karakter:
 * spaties worden overgeslagen, een **volledig** lidwoord (gevolgd door spatie)
 * wordt als filler overgeslagen, en het overige wordt vergeleken met de
 * volgende real-char van `target`. Een laatste partiële invoer die toevallig
 * met een lidwoord samenvalt (bv. `"A"` voor target `"Awakening"`) wordt
 * **niet** als filler beschouwd, zodat de hint correct vooruit kan stappen.
 */
function countMatchedRealChars(current: string, target: string): number {
  const normalizedCurrent = normalizeMemoryInput(current);
  const targetRealChars = stripFillerWords(normalizeMemoryInput(target)).replace(/ /g, '');

  let currentPos = 0;
  let realMatched = 0;
  while (currentPos < normalizedCurrent.length && realMatched < targetRealChars.length) {
    if (normalizedCurrent.charAt(currentPos) === ' ') {
      currentPos++;
      continue;
    }
    if (isAtCompleteFillerWord(normalizedCurrent, currentPos)) {
      currentPos = endOfWordPosition(normalizedCurrent, currentPos);
      continue;
    }
    if (normalizedCurrent.charAt(currentPos) !== targetRealChars.charAt(realMatched)) {
      break;
    }
    currentPos++;
    realMatched++;
  }
  return realMatched;
}

/**
 * `true` wanneer `pos` in `normalized` op een volledig (door spatie of
 * eind-van-string afgesloten, en door spatie of begin-van-string voorafgegaan)
 * lidwoord staat. Een laatste partiële invoer wordt nooit als compleet
 * lidwoord beschouwd zodat het vergelijken karakter-voor-karakter blijft
 * doorgaan zolang de speler nog typt.
 */
function isAtCompleteFillerWord(normalized: string, pos: number): boolean {
  if (pos > 0 && normalized.charAt(pos - 1) !== ' ') {
    return false;
  }
  const wordEnd = endOfWordPosition(normalized, pos);
  if (wordEnd >= normalized.length) {
    return false;
  }
  return FILLER_WORDS.has(normalized.substring(pos, wordEnd));
}

/** Aantal "echte" (niet-filler, niet-whitespace) letters in `target`. */
function countRealChars(target: string): number {
  return stripFillerWords(normalizeMemoryInput(target)).replace(/ /g, '').length;
}

/**
 * `true` wanneer `pos` in `target` op een woord-startpositie staat én dat
 * woord een Nederlands lidwoord is. Een woord begint op `pos === 0` of
 * direct na een spatie.
 */
function isAtFillerWordStart(target: string, pos: number): boolean {
  if (pos > 0 && target.charAt(pos - 1) !== ' ') {
    return false;
  }
  const wordEnd = endOfWordPosition(target, pos);
  const word = normalizeMemoryInput(target.substring(pos, wordEnd));
  return FILLER_WORDS.has(word);
}

/** Index van de eerste spatie op of na `pos`, of `target.length` als die er niet is. */
function endOfWordPosition(target: string, pos: number): number {
  let i = pos;
  while (i < target.length && target.charAt(i) !== ' ') {
    i++;
  }
  return i;
}
